import React, {useEffect} from "react";
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { Check as CheckIcon } from '@vedmarket/ui-kit';

import AlertBottom from "components/common/alertBottom";

import { useLazyGetFinalReportFieldsQuery } from "../../../../../../../../api/reports";
import Preloader from "../../../../../../../common/preloader";
import {getFinalReportMutation} from "../../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../../utils/hooks";
import './../../modal_settings.scss';

type FormValues = {
  fields: {
    code: string;
    checked: boolean;
  }[];
  fileName?: string;
};

type Props = {
  onClose(): void,
}

const ModalSettingsNew = ({onClose} : Props) => {

  const { t } = useTranslation()

  const [ openAlert, setOpenAlert ] = React.useState(false);

  const [ reportRequest, { data: fieldsReport, isSuccess, isLoading } ] = useLazyGetFinalReportFieldsQuery();

  const [ getFinalReport, { isLoading: isReportLoading } ] = getFinalReportMutation()

  const orderId = useGetCalcIdFromRoute()

  const { control, getValues, reset } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { fields: [] },
  });

  useEffect(() => {
    reportRequest();
  }, []);

  useEffect(() => {
    if (isSuccess && fieldsReport) {
      const DATA = transformFieldsReport(fieldsReport);

      const defaultValues: FormValues = {
        fields: DATA.map((field, index) => ({
          code: field.code,
          checked: index === 0,
        })),
      };

      reset(defaultValues);
    }
  }, [ isSuccess, fieldsReport, reset ]);

  const handleDownloadReport = async () => {
    const values = getValues();

    const dataToSend = prepareSelectedFieldsForDownload(values);

    try {
      const { blob, filename } = await getFinalReport({ orderId, ...dataToSend }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = values.fileName || filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setOpenAlert(true);

    } catch (error) {
      console.error("Download failed:", error);
    }

  }

  const transformFieldsReport = (fieldsReport: ReportField[]): ReportField[] => {
    return [ ...fieldsReport ].sort((left, right) => left.order - right.order);
  };

  const prepareSelectedFieldsForDownload = (formValues: FormValues) => {
    const selected_sheets: string[] = [];

    formValues.fields.forEach(field => {
      if (!field.checked) return;

      selected_sheets.push(field.code);
    });

    return { selected_sheets };
  };

  if (isLoading) {
    return <div className='modal_settings_preloader'>
      <Preloader />
    </div>;
  }

  const DATA = transformFieldsReport(fieldsReport || []);

  return <>
    <div className='modal_settings_new'>
      <div className='modal_settings_new__item modal_settings_new__item_isMain'>
        <Typography variant='h6'>{DATA[0]?.name}</Typography>
        <Controller
          name='fileName'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant='outlined'
              label={t('common__text__name')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ marginTop: '28px' }}
              name='filename'
            />
          )}
        />
        <ul className='modal_settings_new__list'>
          {DATA.map((field, fieldIndex) => (
            <li key={field.code} className='modal_settings_new__label'>
              <FormControlLabel
                control={
                  <Controller
                    name={`fields.${fieldIndex}.checked`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        disabled={fieldIndex === 0}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    )}
                  />
                }
                sx={{ marginLeft: '4px', alignItems: 'start' }}
                label={field.name}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className='modal_settings__buttons'>
      <Button
        variant='outlined'
        sx={{ minWidth: '238px', marginRight: 3}}
        onClick={onClose}
      >{t('actions__text__cancel')}</Button>
      <Button
        variant='contained'
        onClick={handleDownloadReport}
        disabled={isReportLoading}
        sx={{ width: '474px' }}
        startIcon={isReportLoading ? <CircularProgress size={20} color='inherit'/> : <CheckIcon/>}
      >{t('common__text__download')}</Button>
    </div>
    <AlertBottom
      open={openAlert}
      onClose={() => setOpenAlert(false)}
      message={t('calc__text__summary__calculationIsCreated')}
    />
  </>
}

export default ModalSettingsNew
